/**
 * Design tokens + primitives for the influencer profile (insight) page.
 * Presentation only — every component keeps its own data logic and simply
 * renders inside these shells so the whole page reads as one system.
 */
import { makeStyles } from '@material-ui/core/styles';

export const ui = {
  pageBg: '#F4F5FA',
  card: '#FFFFFF',
  border: '#E8EAF2',
  borderSoft: '#EEF0F6',
  radius: 16,
  radiusSm: 12,
  shadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.05)',
  shadowHover: '0 2px 4px rgba(16, 24, 40, 0.06), 0 16px 32px rgba(16, 24, 40, 0.10)',
  ink: '#101828',
  inkSoft: '#475467',
  inkFaint: '#98A2B3',
  primary: '#4F46E5',
  primarySoft: '#EEF2FF',
  tileBg: '#F8FAFC',
  font: "'Nunito Sans', sans-serif",
};

export function platformPalette(platform) {
  if (platform === 'youtube') {
    return {
      key: 'youtube',
      label: 'YouTube',
      accent: '#DC2626',
      soft: '#FEF2F2',
      cover: 'linear-gradient(115deg, #14121F 0%, #3D1226 55%, #B91C1C 100%)',
    };
  }
  return {
    key: 'instagram',
    label: 'Instagram',
    accent: '#DB2777',
    soft: '#FDF2F8',
    cover: 'linear-gradient(115deg, #312E81 0%, #86198F 55%, #DB2777 100%)',
  };
}

/** Compact, human counter: 1.2K / 3.4M / 1.1B; leaves non-numeric input alone. */
export function fmtCount(value) {
  if (value == null || value === '' || value === '–') return '–';
  const num = Number(String(value).replace(/,/g, ''));
  if (isNaN(num)) return value;
  const abs = Math.abs(num);
  const trim = (n) => {
    const r = Math.round(n * 10) / 10;
    return Number.isInteger(r) ? String(r) : r.toFixed(1);
  };
  if (abs >= 1e9) return `${trim(num / 1e9)}B`;
  if (abs >= 1e6) return `${trim(num / 1e6)}M`;
  if (abs >= 1e3) return `${trim(num / 1e3)}K`;
  return Number.isInteger(num) ? num.toLocaleString() : String(Math.round(num * 100) / 100);
}

/**
 * Uniform section shell: icon chip + title on the left, optional actions on
 * the right, content below. Replaces the old flat purple header bars.
 */
export function SectionCard({ icon, title, subtitle, actions, children, bodyPadding = 24, style }) {
  // Plain divs on purpose: a globally-leaked stylesheet
  // (CompetitorReportAnalysis.style.css) turns every <section> into a
  // 100vw/100vh centered flex page.
  return (
    <div
      style={{
        background: ui.card,
        border: `1px solid ${ui.border}`,
        borderRadius: ui.radius,
        boxShadow: ui.shadow,
        overflow: 'hidden',
        fontFamily: ui.font,
        textAlign: 'left',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 24px',
          borderBottom: `1px solid ${ui.borderSoft}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {icon && (
            <div
              style={{
                width: 36,
                height: 36,
                flex: '0 0 auto',
                borderRadius: 10,
                background: ui.primarySoft,
                color: ui.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: ui.ink, letterSpacing: 0.2 }}>
              {title}
            </div>
            {subtitle && (
              <div style={{ fontSize: 12, color: ui.inkFaint, marginTop: 2 }}>{subtitle}</div>
            )}
          </div>
        </div>
        {actions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {actions}
          </div>
        )}
      </div>
      <div style={{ padding: bodyPadding }}>{children}</div>
    </div>
  );
}

/** Small titled panel used for chart tiles inside a SectionCard. */
export function ChartTile({ icon, title, right, height, children, style }) {
  return (
    <div
      style={{
        background: ui.card,
        border: `1px solid ${ui.border}`,
        borderRadius: ui.radiusSm,
        padding: 20,
        height,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: ui.font,
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {icon && (
            <span style={{ display: 'inline-flex', color: ui.primary, flex: '0 0 auto' }}>{icon}</span>
          )}
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 800,
              color: ui.ink,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {title}
          </span>
        </div>
        {right}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

const useSkeletonStyles = makeStyles({
  '@keyframes insightShimmer': {
    '0%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0 50%' },
  },
  skeleton: {
    display: 'block',
    background: 'linear-gradient(90deg, #E9EBF3 25%, #F6F7FB 37%, #E9EBF3 63%)',
    backgroundSize: '400% 100%',
    animation: '$insightShimmer 1.4s ease infinite',
  },
});

/** Shimmering placeholder bar/circle for loading states. */
export function Skeleton({ width = '100%', height = 14, radius = 8, circle = false, style }) {
  const classes = useSkeletonStyles();
  return (
    <span
      className={classes.skeleton}
      style={{
        width: circle ? height : width,
        height,
        borderRadius: circle ? '50%' : radius,
        flex: '0 0 auto',
        ...style,
      }}
    />
  );
}

export function EmptyState({ children }) {
  return (
    <div
      style={{
        height: '100%',
        minHeight: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: ui.inkFaint,
        fontSize: 13,
        fontFamily: ui.font,
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}
